"use client"

import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { presentationModeAPI } from "@/lib/api"
import { usePathname } from "next/navigation"

interface PresentationModeContextType {
  isPresentation: boolean
  togglePresentationMode: () => void
  isLoading: boolean
  hidePrices: boolean
  setHidePrices: (value: boolean) => void
}

const PresentationModeContext = createContext<PresentationModeContextType | undefined>(undefined)

// Storage helper for price visibility preference
const HIDE_PRICES_STORAGE_KEY = "pkv2_hide_prices_v1"

const getStoredHidePrices = (): boolean => {
  try {
    return sessionStorage.getItem(HIDE_PRICES_STORAGE_KEY) === "true"
  } catch {
    return false // Default to showing prices
  }
}

const setStoredHidePrices = (hidePrices: boolean): void => {
  try {
    sessionStorage.setItem(HIDE_PRICES_STORAGE_KEY, String(hidePrices))
  } catch {
    // Silently fail if sessionStorage is not available
  }
}

export function PresentationModeProvider({ children }: { children: ReactNode }) {
  const [isPresentation, setIsPresentation] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hidePrices, setHidePricesState] = useState<boolean>(() => getStoredHidePrices())
  const pathname = usePathname()
  
  // Check if we're on a public page (login, reset password, etc.)
  const isPublicPage =
    pathname === "/login" ||
    pathname?.startsWith("/reset-password") ||
    pathname?.startsWith("/register") ||
    pathname === "/forgot-password"

  // Load presentation mode status from API on initial mount
  useEffect(() => {
    const fetchPresentationMode = async () => {
      // Skip fetching if on a public page like login
      if (isPublicPage) {
        setIsLoading(false)
        return
      }
      
      try {
        setIsLoading(true)
        const { isPresentation: status } = await presentationModeAPI.getStatus()
        setIsPresentation(status)
      } catch (error) {
        console.error("Error fetching presentation mode status:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPresentationMode()
  }, [isPublicPage, pathname])

  const togglePresentationMode = async () => {
    // Don't attempt to toggle if on a public page
    if (isPublicPage) return
    
    try {
      setIsLoading(true)
      const newStatus = !isPresentation
      const { isPresentation: updatedStatus } = await presentationModeAPI.toggleStatus(newStatus)
      setIsPresentation(updatedStatus)
    } catch (error) {
      console.error("Error toggling presentation mode:", error)
      // Revert the state if the API call fails
      setIsPresentation(isPresentation)
    } finally {
      setIsLoading(false)
    }
  }

  const setHidePrices = (value: boolean) => {
    setHidePricesState(value)
    setStoredHidePrices(value)
  }

  return (
    <PresentationModeContext.Provider value={{ isPresentation, togglePresentationMode, isLoading, hidePrices, setHidePrices }}>
      {children}
    </PresentationModeContext.Provider>
  )
}

export function usePresentationMode() {
  const context = useContext(PresentationModeContext)
  if (context === undefined) {
    throw new Error("usePresentationMode must be used within a PresentationModeProvider")
  }
  return context
} 